import { Notification, Progress, Provider } from '@eleveo/components-react';
import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Redirect, Route, Switch, matchPath, useHistory, useLocation } from 'react-router';
import { BrowserRouter, RouteComponentProps, withRouter } from 'react-router-dom';

import { Permissions, useAuth } from '../app/AuthorizationProvider';
import { useConfig } from '../app/ConfigurationProvider';
import { Applications } from '../components/Drawer/config';
import { Layout } from '../components/LayoutNext/Layout';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { Layout as LayoutOld } from '../components/layout/Layout';
import { Logout } from '../components/layout/Navigation/Logout';
import { useSidebar } from '../components/layout/Sidebar';
import { useGetAvailableApplicationsQuery } from '../graphql/components';
import { AccessForbidden } from '../scenes/AccessForbidden/AccessForbidden';
import { AdherenceHistoryScene } from '../scenes/AdherenceHistory/AdherenceHistoryScene';
import { AdherenceSettingsScene } from '../scenes/AdherenceSettings/AdherenceSettingsScene';
import { AgentsScene } from '../scenes/Agents/AgentsScene';
import { DashboardScene } from '../scenes/Dashboard/DashboardScene';
import { DataImportScene } from '../scenes/DataImport/DataImportScene';
import { CreateForecastContainer } from '../scenes/ForecastingStudio/CreateForecast';
import { ForecastingStudioScene } from '../scenes/ForecastingStudio/ForecastingStudioScene';
import { IntradayManagementScene } from '../scenes/Intraday/IntradayManagementScene';
import { NotFound } from '../scenes/NotFound/NotFound';
import { QueuesScene } from '../scenes/Queues/QueuesScene';
import { RealTimeAdherenceScene } from '../scenes/RealTimeAdherence/RealTimeAdherenceScene';
import { ReportsAdherenceScene } from '../scenes/Reports/ReportsAdherenceScene';
import { RequestsScene } from '../scenes/Requests/RequestsScene';
import { SchedulingAgentsMonthlyViewScene } from '../scenes/Scheduling/AgentsView/SchedulingAgentsMonthlyViewScene';
import { SchedulingAgentsWeeklyViewScene } from '../scenes/Scheduling/AgentsView/SchedulingAgentsWeeklyViewScene';
import { CreateScheduleSceneContainer } from '../scenes/Scheduling/CreateScheduleSceneContainer';
import { SchedulingComposerScene } from '../scenes/Scheduling/SchedulingComposer/SchedulingComposerScene';
import { SchedulingStudioScene } from '../scenes/Scheduling/SchedulingStudioScene';
import { ShiftTemplatesScene } from '../scenes/ShiftTemplates/ShiftTemplatesScene';
import { SkillsScene } from '../scenes/Skills/SkillsScene';
import { ExternalRedirect } from './ExternalRedirect';
import { ProtectedRoute } from './ProtectedRoute';
import {
  PATH_ACCESS_FORBIDDEN,
  PATH_ADMINISTRATION_DATA_IMPORT,
  PATH_ADMINISTRATION_PEOPLE,
  PATH_ADMINISTRATION_QUEUES,
  PATH_ADMINISTRATION_SHIFTS,
  PATH_ADMINISTRATION_SKILLS,
  PATH_FORECASTING_STUDIO,
  PATH_INTRADAY_MANAGEMENT,
  PATH_LOGOUT,
  PATH_MAIN_ROUTE,
  PATH_MANAGE_ACCOUNT,
  PATH_MY_SCHEDULE,
  PATH_MY_SCHEDULE_MONTHLY,
  PATH_MY_SCHEDULE_WEEKLY,
  PATH_QUALITY_MANAGEMENT,
  PATH_REAL_TIME_ADHERENCE,
  PATH_REPORTS_ADHERENCE,
  PATH_REQUESTS,
  PATH_SCHEDULE_ADHERENCE_HISTORY,
  PATH_SCHEDULE_ADHERENCE_SETTINGS,
  PATH_SCHEDULING_STUDIO,
  PATH_USER_MANAGEMENT,
} from './routes';

type RouteProps = RouteComponentProps & { isNextLayout?: boolean };

const Routes = ({ history, isNextLayout, location }: RouteProps) => {
  const { userHasPermissions } = useAuth();
  const { accountManagementUrl, userManagementUrl, qualityManagementUrl } = useConfig();
  const { resetSidebar } = useSidebar();

  const { data: availableApplicationsData, loading: isLoadingAvailableApplications } = useGetAvailableApplicationsQuery(
    {
      skip: !userHasPermissions([Permissions.ViewAppSwitcher]),
    }
  );

  const availableApplicationNames = useMemo(
    () => availableApplicationsData?.availableApplications.map(({ applicationName }) => applicationName) ?? [],
    [availableApplicationsData]
  );

  useEffect(() => {
    const unlisten = history.listen(({ pathname }) => {
      if (pathname !== location.pathname) {
        /**
         * TODO: solve expanded sidebar and ID change in URL (e.g. editing different agents on People page)
         * @see https://confluence.zoomint.com/display/~Roman.Vesely/WFM+Sidebar+expanded+reset
         */
        resetSidebar();
      }
    });

    return unlisten;
  });

  if (isLoadingAvailableApplications) {
    // this prevents early redirect to Access Forbidden page when availableApplicationsData were not loaded, yet
    if (isNextLayout) {
      return <Progress withOverlay />;
    }

    return <LoadingOverlay loading />;
  }

  return (
    <Switch>
      <Route exact path={PATH_MAIN_ROUTE} component={DashboardScene} />
      <ProtectedRoute
        exact
        path={`${PATH_ADMINISTRATION_PEOPLE}/:action(edit)`}
        component={() => <Redirect to={PATH_ADMINISTRATION_PEOPLE} />}
        permissions={[Permissions.EditAgent, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_ADMINISTRATION_PEOPLE}
        component={AgentsScene}
        permissions={[Permissions.ViewAgent, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_ADMINISTRATION_SKILLS}
        component={SkillsScene}
        permissions={[Permissions.ViewSkill]}
      />
      <ProtectedRoute
        exact
        path={PATH_ADMINISTRATION_SHIFTS}
        component={ShiftTemplatesScene}
        permissions={[Permissions.ViewShiftTemplate]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_ADMINISTRATION_QUEUES}/:action(edit)`}
        component={() => <Redirect to={PATH_ADMINISTRATION_QUEUES} />}
        permissions={[Permissions.EditQueue, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_ADMINISTRATION_QUEUES}/:action(create)`}
        component={() => <Redirect to={PATH_ADMINISTRATION_QUEUES} />}
        permissions={[Permissions.CreateQueue, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_ADMINISTRATION_QUEUES}
        component={QueuesScene}
        permissions={[Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_ADMINISTRATION_DATA_IMPORT}
        component={DataImportScene}
        permissions={[Permissions.FileUpload]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_SCHEDULING_STUDIO}/:action(create)/composer`}
        component={SchedulingComposerScene}
        permissions={[Permissions.CreateSchedule, Permissions.ViewAgent, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_SCHEDULING_STUDIO}/:action(edit)/composer/:id(\\d+)`}
        component={SchedulingComposerScene}
        permissions={[Permissions.EditSchedule, Permissions.ViewAgent, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_SCHEDULING_STUDIO}/:action(view)/composer/:id(\\d+)`}
        component={SchedulingComposerScene}
        permissions={[Permissions.ViewSchedule, Permissions.ViewAgent, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_SCHEDULING_STUDIO}/:action(create)`}
        component={CreateScheduleSceneContainer}
        permissions={[Permissions.CreateSchedule, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_SCHEDULING_STUDIO}/:action(edit)/:id(\\d+)`}
        component={CreateScheduleSceneContainer}
        permissions={[Permissions.EditSchedule, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_SCHEDULING_STUDIO}/:action(view)/:id(\\d+)`}
        component={CreateScheduleSceneContainer}
        permissions={[Permissions.ViewSchedule, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        path="/scheduling-lab"
        component={() => <Redirect to={PATH_SCHEDULING_STUDIO} />}
        permissions={[Permissions.ViewSchedule, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_SCHEDULING_STUDIO}
        component={SchedulingStudioScene}
        permissions={[Permissions.ViewSchedule, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_REQUESTS}/:typeKey?/:requestId?`}
        component={RequestsScene}
        permissions={[Permissions.ViewScheduleRequests]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_FORECASTING_STUDIO}/:action(create)`}
        component={CreateForecastContainer}
        permissions={[Permissions.CreateForecast, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_FORECASTING_STUDIO}/:action(view)/:id(\\d+)`}
        component={CreateForecastContainer}
        permissions={[Permissions.ViewForecast, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_FORECASTING_STUDIO}/:action(edit)/:id(\\d+)`}
        component={CreateForecastContainer}
        permissions={[Permissions.EditForecast, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_FORECASTING_STUDIO}
        component={ForecastingStudioScene}
        permissions={[Permissions.ViewForecast, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        path="/forecasting-lab"
        component={() => <Redirect to={PATH_FORECASTING_STUDIO} />}
        permissions={[Permissions.ViewForecast, Permissions.ViewQueue]}
      />
      <ProtectedRoute
        exact
        path={PATH_MY_SCHEDULE_MONTHLY}
        component={SchedulingAgentsMonthlyViewScene}
        permissions={[Permissions.ViewAgentSchedule]}
      />
      <ProtectedRoute
        exact
        path={PATH_MY_SCHEDULE_WEEKLY}
        component={SchedulingAgentsWeeklyViewScene}
        permissions={[Permissions.ViewAgentSchedule]}
      />
      <ProtectedRoute
        exact
        path={PATH_MY_SCHEDULE}
        component={() => <Redirect to={PATH_MY_SCHEDULE_MONTHLY} />}
        permissions={[Permissions.ViewAgentSchedule]}
      />
      <ProtectedRoute
        exact
        path={PATH_SCHEDULE_ADHERENCE_SETTINGS}
        component={AdherenceSettingsScene}
        permissions={[Permissions.ViewAdherenceSettings]}
      />
      <ProtectedRoute
        exact
        path={PATH_SCHEDULE_ADHERENCE_HISTORY}
        component={AdherenceHistoryScene}
        permissions={[Permissions.ViewAdherenceHistory]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_REAL_TIME_ADHERENCE}/:selectedAgentId?`}
        component={RealTimeAdherenceScene}
        permissions={[Permissions.ViewRealTimeAdherence]}
      />
      <ProtectedRoute
        exact
        path={`${PATH_INTRADAY_MANAGEMENT}/:queueId(\\d+)?`}
        component={IntradayManagementScene}
        permissions={[Permissions.ViewIntraday]}
      />
      <ProtectedRoute
        exact
        path={PATH_QUALITY_MANAGEMENT}
        component={() => <ExternalRedirect to={qualityManagementUrl} />}
        customPermissionCheck={() => availableApplicationNames.includes(Applications.QM)}
      />
      <ProtectedRoute
        exact
        path={PATH_REPORTS_ADHERENCE}
        component={ReportsAdherenceScene}
        permissions={[Permissions.ViewReports]}
      />
      <ProtectedRoute
        exact
        path={PATH_USER_MANAGEMENT}
        component={() => <ExternalRedirect to={userManagementUrl} />}
        customPermissionCheck={() => availableApplicationNames.includes(Applications.UM)}
      />
      <ProtectedRoute
        exact
        path={PATH_MANAGE_ACCOUNT}
        component={() => <ExternalRedirect to={accountManagementUrl} />}
        permissions={[]}
      />
      <Route exact path={PATH_LOGOUT}>
        <Logout />
      </Route>
      <Route exact path={PATH_ACCESS_FORBIDDEN} component={AccessForbidden} />
      <Route component={NotFound} />
    </Switch>
  );
};

const RoutesWithProps = (routeProps: RouteProps) => <Routes {...routeProps} />;
const WFMRoutes = withRouter(RoutesWithProps);

const RouterContent = () => {
  const { locale } = useAuth();
  const { pathname } = useLocation();
  const { push } = useHistory();

  if (
    matchPath(pathname, {
      exact: true,
      path: [
        PATH_ADMINISTRATION_DATA_IMPORT,
        PATH_ADMINISTRATION_PEOPLE,
        PATH_ADMINISTRATION_QUEUES,
        PATH_ADMINISTRATION_SKILLS,
        PATH_MY_SCHEDULE_MONTHLY,
        `${PATH_INTRADAY_MANAGEMENT}/:queueId(\\d+)?`,
        PATH_ADMINISTRATION_SHIFTS,
        `${PATH_REQUESTS}/:typeKey?/:requestId?`,
        PATH_FORECASTING_STUDIO,
        PATH_REPORTS_ADHERENCE,
      ],
    })
  ) {
    return (
      <Provider locale={locale} router={{ navigate: push }}>
        <Layout>
          <WFMRoutes isNextLayout />
        </Layout>
      </Provider>
    );
  }

  return (
    <LayoutOld>
      <WFMRoutes />
    </LayoutOld>
  );
};

export const Router = () => {
  const { basePath } = useConfig();

  return (
    <BrowserRouter basename={basePath}>
      {createPortal(<Notification />, document.body)}
      <RouterContent />
    </BrowserRouter>
  );
};
